import { useMemo, useState } from 'react';
import {
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';

import ChartColumnStacked from 'src/sections/charts/chart-column-stacked';
import ChartColumnStackedPercent from 'src/sections/charts/chart-collumn-stacked-percent';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { brandedCampaignsVisibility } from 'src/utils/branded-campaigns';

import {
  CHART_HEIGHT,
  CHART_INNER_HEIGHT,
  TRAFFIC_BRANDED_FORMAT,
  formatLabels,
  getDistributionColors,
} from './constants';

interface FunnelDistributionChartProps {
  kpisData: any;
  kpisError: any;
  isFetchingKpis: boolean;
  selectedDisplayGraph: string;
  setSelectedDisplayGraphValue: (value: string) => void;
}

export default function FunnelDistributionChart({
  kpisData,
  kpisError,
  isFetchingKpis,
  selectedDisplayGraph,
  setSelectedDisplayGraphValue,
}: FunnelDistributionChartProps) {
  const { t, currentLang } = useLocales();
  const filterStore = useSelector((state: any) => state.filters);
  const workspace: any = useCurrentWorkspace();
  const [showLabels, setShowLabels] = useState(false);

  // Les séries Shopping / PMax n'apparaissent que si les campagnes brandées
  // correspondantes sont configurées dans le workspace (la légende suit les séries).
  const { showShopping, showPmax } = brandedCampaignsVisibility(workspace);

  const distributionChart = useMemo(() => {
    const chart: any[] = [];

    const hasPmaxClicksData = showPmax && kpisData?.time_series_data?.some(
      (item: any) => (item?.adClicksPmax ?? 0) > 0
    );

    if (workspace?.template !== 'E-commerce' && !workspace?.shoppingIncluded) {
      chart.push(
        {
          name: t('dashboard.brandTraffic.organicBrandTraffic'),
          data:
            kpisData?.time_series_data?.map(
              (item: any) => item?.organicClicks ?? 0
            ) ?? [],
        },
        {
          name: t('dashboard.brandTraffic.searchPaid'),
          data:
            kpisData?.time_series_data?.map(
              (item: any) => item?.adClicks ?? 0
            ) ?? [],
        }
      );

      if (hasPmaxClicksData) {
        chart.push({
          name: t('dashboard.brandTraffic.pmaxPaid'),
          data:
            kpisData?.time_series_data?.map(
              (item: any) => item?.adClicksPmax ?? 0
            ) ?? [],
        });
      }

      chart.push({
        name: t('dashboard.brandTraffic.uncapturedBrandTraffic'),
        data:
          kpisData?.time_series_data?.map(
            (item: any) => item?.uncapturedBrandTraffic ?? 0
          ) ?? [],
      });
    } else {
      chart.push(
        {
          name: t('dashboard.brandTraffic.organicBrandTraffic'),
          data:
            kpisData?.time_series_data?.map(
              (item: any) => item?.organicClicks ?? 0
            ) ?? [],
        },
        {
          name: t('dashboard.brandTraffic.searchPaid'),
          data:
            kpisData?.time_series_data?.map(
              (item: any) => item?.adClicksSearch ?? 0
            ) ?? [],
        }
      );

      if (showShopping) {
        chart.push({
          name: t('dashboard.brandTraffic.shoppingPaid'),
          data:
            kpisData?.time_series_data?.map(
              (item: any) => item?.adClicksShopping ?? 0
            ) ?? [],
        });
      }

      if (hasPmaxClicksData) {
        chart.push({
          name: t('dashboard.brandTraffic.pmaxPaid'),
          data:
            kpisData?.time_series_data?.map(
              (item: any) => item?.adClicksPmax ?? 0
            ) ?? [],
        });
      }

      chart.push({
        name: t('dashboard.brandTraffic.uncapturedBrandTraffic'),
        data:
          kpisData?.time_series_data?.map(
            (item: any) => item?.uncapturedBrandTraffic ?? 0
          ) ?? [],
      });
    }

    return chart;
  }, [kpisData, workspace?.template, workspace?.shoppingIncluded, showShopping, showPmax, t]);


  const customLabels = formatLabels(
    kpisData?.time_series_data ?? [],
    filterStore.timestep,
    currentLang.value
  );

  const distributionColors = getDistributionColors(distributionChart);

  // Totaux affichés dans le tooltip. `compute` reçoit les valeurs live des séries
  // au point survolé (même source que les lignes par-série → jamais périmé) :
  //  - Brand Traffic  = somme des clics captés = sum(ad_clicks_from_*) + sum(organic_clicks)
  //                     (toutes les séries SAUF le trafic non capturé)
  //  - Brand Searches = Brand Traffic + trafic non capturé (= somme de toute la pile)
  const brandTotalsRows = useMemo(() => {
    const uncapturedName = t('dashboard.brandTraffic.uncapturedBrandTraffic');
    const sum = (values: number[]) => values.reduce((acc, v) => acc + (v || 0), 0);
    return [
      {
        name: t('dashboard.brandTraffic.totalBrandTraffic'),
        color: '#637381', // Figma — Brand Traffic marker (= catalog total_brand_traffic)
        compute: (values: number[], names: string[]) =>
          values.reduce((acc, v, i) => (names[i] === uncapturedName ? acc : acc + (v || 0)), 0),
      },
      {
        name: t('dashboard.brandTraffic.brandSearches'),
        color: '#EE7A63', // Figma — Brand Searches marker (coral)
        compute: (values: number[]) => sum(values),
      },
    ];
  }, [t]);

  return (
    <Paper
      sx={{
        backgroundColor: 'common.white',
        boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
        border: '1px solid #eaeaea',
        height: `${CHART_HEIGHT}px`,
        borderRadius: '16px',
      }}
    >
      {isFetchingKpis || (!kpisData && !kpisError) ? (
        <Skeleton
          width="100%"
          height="100%"
          style={{ borderRadius: 10 }}
          variant="rectangular"
          animation="wave"
        >
          <Iconify py="auto" icon="eva:checkmark-circle-2-fill" color="success.main" />
          <Typography color="success.main">Active</Typography>
        </Skeleton>
      ) : (
        <div style={{ padding: '24px' }}>
          <Stack spacing={2} sx={{ mb: 0 }}>
            <div
              style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
            >
              <div>
                <Typography
                  variant="overline"
                  sx={{ color: '#919eab', display: 'block', lineHeight: 1.4 }}
                >
                  {t('dashboard.brandTraffic.organicTrafficVsAdTraffic')}
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2, color: '#1c252e' }}>
                  {t('dashboard.brandTraffic.branded_traffic_distribution')}
                </Typography>
              </div>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showLabels}
                      onChange={(event) => setShowLabels(event.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#212B36', opacity: 1 },
                      }}
                    />
                  }
                  label={t('dashboard.brandTraffic.showLabels')}
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': { fontSize: 13, color: '#637381' },
                  }}
                />
                <TextField
                  sx={{ width: 130 }}
                  size="small"
                  variant="outlined"
                  select
                  value={selectedDisplayGraph}
                  onChange={(event) => setSelectedDisplayGraphValue(event.target.value)}
                  SelectProps={{
                    renderValue: (value) => {
                      const option = TRAFFIC_BRANDED_FORMAT.find((o) => o.value === value);
                      return (
                        <Chip
                          label={t(option?.label ?? '')}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(145, 158, 171, 0.12)',
                            color: 'rgba(51, 60, 71, 1)',
                            cursor: 'pointer',

                            '&:hover': {
                              backgroundColor: '#DCE0E4',
                            },
                          }}
                        />
                      );
                    },
                  }}
                >
                  {TRAFFIC_BRANDED_FORMAT.map((option, index) => (
                    <MenuItem key={index} value={option.value}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </div>
          </Stack>
          {selectedDisplayGraph === 'Value' ? (
            <ChartColumnStacked
              series={kpisData ? distributionChart : []}
              labels={customLabels}
              height={CHART_INNER_HEIGHT}
              colors={distributionColors}
              barBorderRadius={4}
              showDataLabels={showLabels}
              legendMarkerSize={8}
              legendLabelColor="#637381"
              extraTooltipRows={brandTotalsRows}
            />
          ) : (
            <ChartColumnStackedPercent
              series={kpisData ? distributionChart : []}
              labels={customLabels}
              height={CHART_INNER_HEIGHT}
              colors={distributionColors}
              barBorderRadius={4}
              showDataLabels={showLabels}
              legendMarkerSize={8}
              legendLabelColor="#637381"
              extraTooltipRows={brandTotalsRows}
            />
          )}
        </div>
      )}
    </Paper>
  );
}
